import React, { useMemo } from "react";
import { Overlay, BaseOverlaySchema, AnimationType } from "../Overlay";
import { interpolate, Easing, useCurrentFrame } from "remotion";
import { z } from "zod";

// Extend base schema with SlidingReveal-specific properties
export const Schema = BaseOverlaySchema.extend({
  titleText: z.string().default("Reveal Text").describe("Title text"),
  anchorColor: z.string().default("#FFA500").describe("Anchor rectangle color (hex)"),
  anchorWidth: z.number().default(50).describe("Anchor rectangle width in pixels"),
  revealHeight: z.number().default(60).describe("Reveal animation height in pixels"),
  containerWidth: z.number().default(1080).describe("Container width in pixels (for max width calculation)"),
  revealStartFrame: z.number().default(10).describe("Frame when reveal animation starts"),
  revealDuration: z.number().default(20).describe("Duration of reveal animation in frames"),
  fontSize: z.number().optional().describe("Title font size in pixels (optional - auto-calculated from height if not provided)"),
  fontWeight: z.string().default("700").describe("Font weight"),
});

export const SlidingReveal = ({
  // Base overlay props
  startFrame = 0,
  endFrame = 300,
  inAnimation = AnimationType.FADE,
  inDuration = 20,
  outAnimation = AnimationType.FADE,
  outDuration = 15,
  x = 100,
  y = 100,
  opacity = 1,
  zIndex = 10,
  // SlidingReveal-specific props
  titleText = "Reveal Text",
  anchorColor = "#FFA500",
  anchorWidth = 25,
  revealHeight = 60,
  containerWidth = 1080,
  revealStartFrame = 10,
  revealDuration = 20,
  fontSize = undefined,
  fontWeight = "700",
}) => {
  const currentFrame = useCurrentFrame();

  // Constants
  const TEXT_PADDING = 48; // Padding on both sides of the text content
  const TOTAL_TEXT_PADDING = TEXT_PADDING * 2;
  const MIN_FONT_SIZE = 24;
  const FONT_HEIGHT_RATIO = 0.75;

  // Calculate font size based on height if not provided
  const calculatedFontSize = useMemo(() => {
    return fontSize || Math.max(MIN_FONT_SIZE, Math.round(revealHeight * FONT_HEIGHT_RATIO));
  }, [fontSize, revealHeight]);

  // Calculate dynamic width based on text length and actual container constraints
  const calculatedWhiteBackgroundWidth = useMemo(() => {
    // Approximate character width (rough estimation based on font)
    const avgCharWidth = calculatedFontSize * 0.6;
    const textWidth = titleText.length * avgCharWidth;

    // Total width needed: text + padding on both sides
    const calculatedWidth = textWidth + TOTAL_TEXT_PADDING;

    // Max width is 80% of container width minus anchor width
    const maxWidth = Math.max(TOTAL_TEXT_PADDING, containerWidth * 0.8 - anchorWidth);

    return Math.min(calculatedWidth, maxWidth);
  }, [titleText, calculatedFontSize, containerWidth, anchorWidth]);

  const maxSpanWidth = useMemo(() => {
    return Math.max(0, calculatedWhiteBackgroundWidth - TOTAL_TEXT_PADDING);
  }, [calculatedWhiteBackgroundWidth, TOTAL_TEXT_PADDING]);

  // Calculate reveal progress (0 to 1)
  const revealProgress = useMemo(() => {
    // Calculate frame position relative to the start of the overlay
    const frameInOverlay = currentFrame - startFrame;
    return Math.min(1, Math.max(0, (frameInOverlay - revealStartFrame) / revealDuration));
  }, [currentFrame, startFrame, revealStartFrame, revealDuration]);

  // Calculate the slide offset for the white background
  const slideOffset = useMemo(() => {
    return interpolate(revealProgress, [0, 1], [-(calculatedWhiteBackgroundWidth + anchorWidth), 0], {
      easing: Easing.out(Easing.cubic),
    });
  }, [revealProgress, calculatedWhiteBackgroundWidth, anchorWidth]);

  return (
    <Overlay
      startFrame={startFrame}
      endFrame={endFrame}
      inAnimation={inAnimation}
      inDuration={inDuration}
      outAnimation={outAnimation}
      outDuration={outDuration}
      x={x}
      y={y}
      opacity={opacity}
      zIndex={zIndex}
      style={{
        position: "relative",
      }}
    >
      {/* Container for the reveal effect */}
      <div
        style={{
          position: "relative",
          width: anchorWidth + calculatedWhiteBackgroundWidth,
          height: revealHeight,
          overflow: "hidden",
        }}
      >
        {/* Anchor rectangle - always visible on the left */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: anchorWidth,
            height: revealHeight,
            backgroundColor: anchorColor,
            zIndex: 10,
          }}
        />

        {/* White background with text - slides in from left */}
        <div
          style={{
            position: "absolute",
            left: slideOffset,
            top: 0,
            width: calculatedWhiteBackgroundWidth + anchorWidth,
            height: revealHeight,
            backgroundColor: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            paddingLeft: `${anchorWidth + TEXT_PADDING}px`,
            //paddingRight: `${TEXT_PADDING}px`,
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              fontFamily: "Inter, -apple-system, Roboto, Helvetica, sans-serif",
              fontWeight: fontWeight,
              fontSize: `${calculatedFontSize}px`,
              color: "#000000",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: `${maxSpanWidth}px`,
            }}
          >
            {titleText}
          </span>
        </div>
      </div>
    </Overlay>
  );
};

export default SlidingReveal;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
